import { useLingui } from '@lingui/react/macro';
import { useState, type ReactNode } from 'react';

import type { ApiError } from '@/shared/http';
import { resolvePatternText } from '@/shared/patterns';

import type { StepUpConfirm, StepUpTexts } from './step-up';
import { TransactionSheet } from './transaction-sheet';

export interface StepUpSheetProps<R> {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** A operação sensível: com a prova do `StartAuth` ou com a autorização do step-up novo (`grantConfirm`). */
  readonly confirm: StepUpConfirm<R>;
  /** Erro da operação que quem abriu trata (fecha e devolve à tela). `true` = tratado. */
  readonly onOperationError?: (error: ApiError) => boolean;
  /** Depois do sucesso; o painel já fechou. */
  readonly onDone?: (result: R) => void;
  /** "Voltar" do legado: fecha e volta ao passo anterior da tela. Ausente, o botão é "Cancelar" e só fecha. */
  readonly onBack?: () => void;
  readonly texts?: StepUpTexts;
  /** Resumo do que vai ser confirmado (a Revisão), acima dos campos. */
  readonly description?: ReactNode;
  readonly destructive?: boolean;
}

/**
 * O `ConfirmMfaFormDrawer` do legado (`components/ConfirmMfaForm/ConfirmMfaFormDrawer.tsx`) no Sheet único de transação
 * (W11): painel à direita na web e tela cheia no iPhone, a prévia e o código na mesma etapa, "Confirmar operação". Para
 * a ação do `ActionDialog`, use `withStepUp`; este painel serve a confirmação aberta por código (aprovar, cancelar).
 */
export function StepUpSheet<R>(props: StepUpSheetProps<R>) {
  const { i18n, t } = useLingui();
  const texts = props.texts ?? {};
  const title = texts.title ? resolvePatternText(i18n, texts.title) : t`Confirme a operação`;
  const submitLabel = texts.submitLabel ? resolvePatternText(i18n, texts.submitLabel) : t`Confirmar operação`;
  const { onBack } = props;
  return (
    <TransactionSheet<R>
      open={props.open}
      onOpenChange={props.onOpenChange}
      slot="step-up-sheet"
      title={title}
      steps={props.description ? ['review', 'code'] : ['code']}
      step="code"
      description={props.description}
      confirm={props.confirm}
      onDone={(result) => {
        props.onOpenChange(false);
        props.onDone?.(result);
      }}
      {...(onBack
        ? {
            onCodeBack: () => {
              props.onOpenChange(false);
              onBack();
            },
          }
        : {})}
      submitLabel={submitLabel}
      {...(props.onOperationError ? { onOperationError: props.onOperationError } : {})}
      size="sm"
      {...(props.destructive ? { destructive: true } : {})}
    />
  );
}

interface Pending {
  readonly serial: number;
  readonly props: Omit<StepUpSheetProps<unknown>, 'open' | 'onOpenChange'>;
}

export type StepUpRequest<R> = Omit<StepUpSheetProps<R>, 'open' | 'onOpenChange'>;

export interface StepUpController {
  /** Abre o painel do step-up para uma operação. */
  open<R>(request: StepUpRequest<R>): void;
  readonly isOpen: boolean;
  /** Ponha uma vez na tela. */
  readonly element: ReactNode;
}

/**
 * Um painel de step-up por tela, aberto por código (`stepUp.open({ confirm, onDone, texts })`). O
 * último fica montado depois de fechar, para a saída animar (U38); a senha já foi zerada.
 */
export function useStepUp(): StepUpController {
  const [pending, setPending] = useState<Pending | null>(null);
  const [isOpen, setOpen] = useState(false);
  return {
    open: (request) => {
      setPending((previous) => ({ serial: (previous?.serial ?? 0) + 1, props: request as Pending['props'] }));
      setOpen(true);
    },
    isOpen,
    element: pending ? <StepUpSheet key={pending.serial} {...pending.props} open={isOpen} onOpenChange={setOpen} /> : null,
  };
}
